import { describePosition, isLinePosition, staffPosition, staffRegion, type Clef } from '../../clef.ts';
import { pitchId, type Accidental, type SpelledPitch } from '../../pitch.ts';
import { createRng, type Rng } from '../../random.ts';
import {
  SKILL_ACCIDENTAL_FLATS,
  SKILL_ACCIDENTAL_SHARPS,
  SKILL_OCTAVE_NAMING,
  clefSkillId,
  regionSkillKind,
} from '../../skills.ts';
import type { GeneratedQuestion } from '../types.ts';
import { candidatePitches, staffClefFor, type AnswerMode, type NoteIdConfig } from './config.ts';

export interface NoteIdPrompt {
  /** Clef of the staff the note is written on (with a grand staff: treble or bass). */
  clef: Clef;
  /** Present when the question must be shown on a grand staff. */
  staff?: 'grand' | undefined;
  /** Written pitch id, e.g. "F#4". The note must be drawn, so the client necessarily knows it. */
  pitch: string;
  answerMode: AnswerMode;
  /** Accidental buttons to offer (always includes "natural"). */
  accidentalChoices: Accidental[];
}

export interface NoteIdExpected {
  clef: Clef;
  pitch: string;
  answerMode: AnswerMode;
}

export type NoteIdQuestion = GeneratedQuestion<NoteIdPrompt, NoteIdExpected>;

export function skillsForPitch(clef: Clef, pitchValue: SpelledPitch, answerMode: AnswerMode): string[] {
  const position = staffPosition(clef, pitchValue);
  const skills = [clefSkillId(clef, regionSkillKind(staffRegion(position), isLinePosition(position)))];
  if (pitchValue.accidental === 'sharp') skills.push(SKILL_ACCIDENTAL_SHARPS);
  if (pitchValue.accidental === 'flat') skills.push(SKILL_ACCIDENTAL_FLATS);
  if (answerMode === 'spelled-octave') skills.push(SKILL_OCTAVE_NAMING);
  return skills;
}

/**
 * Shuffle-bag drawing: every candidate appears once per pass through the bag,
 * so a short session covers the range evenly. The same pitch never appears
 * twice in a row, and (when at least four candidates exist) not within three
 * questions. Bags are refilled independently for naturals and altered notes so
 * `accidentalRate` controls their mix.
 */
class ShuffleBag {
  private bag: SpelledPitch[] = [];
  private readonly items: SpelledPitch[];
  private readonly rng: Rng;

  constructor(items: SpelledPitch[], rng: Rng) {
    this.items = items;
    this.rng = rng;
  }

  get size(): number {
    return this.items.length;
  }

  draw(avoid: ReadonlySet<string>): SpelledPitch {
    if (this.bag.length === 0) this.bag = this.rng.shuffle(this.items);
    let index = this.bag.findIndex((item) => !avoid.has(pitchId(item)));
    if (index === -1) {
      // Everything left in the bag is recently used; pull a fresh non-recent item if one exists.
      const fresh = this.items.filter((item) => !avoid.has(pitchId(item)));
      if (fresh.length > 0) return this.rng.pick(fresh);
      index = 0;
    }
    const [item] = this.bag.splice(index, 1);
    return item as SpelledPitch;
  }
}

export function generateNoteIdQuestions(config: NoteIdConfig, seed: string): NoteIdQuestion[] {
  const candidates = candidatePitches(config);
  if (candidates.length === 0) throw new Error('Configuration produces no notes; validate before generating.');

  const rng = createRng(`note-identification:v1:${seed}`);
  const naturals = new ShuffleBag(
    candidates.filter((c) => c.accidental === 'natural'),
    rng,
  );
  const altered = new ShuffleBag(
    candidates.filter((c) => c.accidental !== 'natural'),
    rng,
  );
  const repeatWindow = candidates.length >= 4 ? 3 : 1;
  const accidentalChoices: Accidental[] = ['natural', ...config.accidentals.filter((a) => a !== 'natural')];
  const recent: string[] = [];
  const questions: NoteIdQuestion[] = [];

  for (let position = 0; position < config.questionCount; position++) {
    let bag: ShuffleBag;
    if (altered.size === 0) bag = naturals;
    else if (naturals.size === 0) bag = altered;
    else bag = rng.next() < config.accidentalRate ? altered : naturals;

    const avoid = new Set(recent.slice(-repeatWindow));
    let chosen = bag.draw(avoid);
    if (avoid.has(pitchId(chosen))) {
      // The chosen bag could only offer a recent pitch; try the other bag.
      const other = bag === naturals ? altered : naturals;
      if (other.size > 0) {
        const alternative = other.draw(avoid);
        if (!avoid.has(pitchId(alternative))) chosen = alternative;
      }
    }
    const id = pitchId(chosen);
    const clef = staffClefFor(config, chosen);
    recent.push(id);
    questions.push({
      position,
      prompt: {
        clef,
        ...(config.grandStaff ? { staff: 'grand' as const } : {}),
        pitch: id,
        answerMode: config.answerMode,
        accidentalChoices: config.answerMode === 'letter' ? ['natural'] : accidentalChoices,
      },
      expected: { clef, pitch: id, answerMode: config.answerMode },
      skillIds: skillsForPitch(clef, chosen, config.answerMode),
    });
  }
  return questions;
}

/** Text used by screen readers for the notation image. Deliberately never names the pitch. */
export function notationAccessibleLabel(prompt: NoteIdPrompt, options: { grand?: boolean } = {}): string {
  if (prompt.staff === 'grand' || options.grand) {
    const which = prompt.clef === 'bass' ? 'lower (bass clef)' : 'upper (treble clef)';
    return `Grand staff with one note on the ${which} staff. Identify the note by reading the staff.`;
  }
  return `Music staff in ${prompt.clef} clef with one note. Identify the note by reading the staff.`;
}

/**
 * Accessible alternative prompt: describes the staff position in words. This
 * turns the task into "name the note at this staff position", which exercises
 * staff knowledge but not visual reading, so sessions answered this way are
 * flagged as using the described-position alternative.
 */
export function describedPositionPrompt(prompt: NoteIdPrompt, pitchValue: SpelledPitch): string {
  const d = describePosition(staffPosition(prompt.clef, pitchValue));
  const accidental =
    pitchValue.accidental === 'sharp' ? ', with a sharp sign' : pitchValue.accidental === 'flat' ? ', with a flat sign' : '';
  const preposition = d.kind === 'space' ? 'in' : 'on';
  const clefName = `${prompt.clef[0]?.toUpperCase()}${prompt.clef.slice(1)} clef`;
  const where = prompt.staff === 'grand' ? `${prompt.clef === 'bass' ? 'Lower' : 'Upper'} staff (${clefName.toLowerCase()})` : clefName;
  return `${where}: a note ${preposition} ${d.text}${accidental}.`;
}
